import type { BillingPeriod, PricingPlan } from "../data/pricingData";
import type { ProductCatalogItem } from "@/_services/membershipServices/payment";

export type PricingTranslateFn = (
  key: string,
  values?: Record<string, string>,
) => string;

/** Paddle price id → numeric amount from `/payment/catalog`. */
export function amountByPriceIdFromCatalog(
  items: ProductCatalogItem[] | undefined,
): Record<string, number> {
  if (!items?.length) return {};

  const map: Record<string, number> = {};
  for (const item of items) {
    map[item.id] = Number.parseFloat(item.amount);
  }
  return map;
}

const usdWhole = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

/** Monthly equivalent when paying yearly (annual total ÷ 12); up to 2 decimals when needed. */
const usdMonthlyEquiv = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 0,
  maximumFractionDigits: 1,
});

export function formatCatalogUsd(amount: number): string {
  return usdWhole.format(amount);
}

export function formatMonthlyEquivalentUsd(annualTotalUsd: number): string {
  return usdMonthlyEquiv.format(annualTotalUsd / 12);
}

function resolvePaidPlanUsd(
  plan: PricingPlan,
  billing: BillingPeriod,
  amountByPriceId: Record<string, number>,
): number {
  const paddlePriceId = plan.pricingId[billing];
  const fallback = billing === "monthly" ? plan.priceMonthly : plan.priceAnnual;

  if (paddlePriceId) {
    const fromCatalog = amountByPriceId[paddlePriceId];
    if (Number.isFinite(fromCatalog)) {
      return fromCatalog;
    }
  }

  return fallback;
}

/**
 * Label shown on pricing cards/table: translated free/custom, otherwise USD from catalog
 * when the plan’s Paddle price id exists in `amountByPriceId`, else plan fallback amounts.
 */
export function formatPlanPriceLabel(
  plan: PricingPlan,
  billing: BillingPeriod,
  amountByPriceId: Record<string, number>,
  t: PricingTranslateFn,
): string {
  if (plan.isCustom) {
    return t("common.custom");
  }

  if (plan.priceMonthly === 0) {
    return t("common.free");
  }

  const usd = resolvePaidPlanUsd(plan, billing, amountByPriceId);
  return formatCatalogUsd(usd);
}

export type AnnualBillingDisplayParts = {
  headlineAmount: string;
  headlineSuffix: string;
  subline: string;
};

/** When billing yearly: big line = equivalent per month, second line = billed annual total. */
export function getAnnualBillingDisplayParts(
  plan: PricingPlan,
  amountByPriceId: Record<string, number>,
  t: PricingTranslateFn,
): AnnualBillingDisplayParts | null {
  if (plan.isCustom || plan.priceMonthly === 0) {
    return null;
  }

  const annualUsd = resolvePaidPlanUsd(plan, "annual", amountByPriceId);

  const totalLabel = formatCatalogUsd(annualUsd);

  return {
    headlineAmount: formatMonthlyEquivalentUsd(annualUsd),
    headlineSuffix: t("common.perMonth"),
    subline: t("common.annualBilledSubline", { total: totalLabel }),
  };
}
